/**
 * Byte figures as the player card and the activity feed print them.
 *
 * Binary units, because the ceiling the operator sets is in MiB and a save
 * that is "10 MiB" on the config page must not read "10.5 MB" on the card.
 * One decimal — "412.3 KiB" — is what the mockup draws; the exact count sits
 * in the gloss beside it, through formatByteCount.
 */

export const KIB = 1024;
export const MIB = 1024 * 1024;

const COUNT = new Intl.NumberFormat("en-US");

/** "422,197 bytes" — the exact figure, with thousands separators. */
export function formatByteCount(n: number): string {
  return `${COUNT.format(n)} bytes`;
}

/** "412.3 KiB", "1.9 MiB", "14.1 MiB"; below a kibibyte, "512 B". */
export function formatBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return "—";
  if (n < KIB) return `${n} B`;
  if (n < MIB) return `${(n / KIB).toFixed(1)} KiB`;
  if (n < MIB * KIB) return `${(n / MIB).toFixed(1)} MiB`;
  return `${(n / (MIB * KIB)).toFixed(1)} GiB`;
}

/** "9f86d081…f00a08" — the first eight and the last six hex characters. */
export function shortSha(sha: string): string {
  if (sha.length <= 16) return sha;
  return `${sha.slice(0, 8)}…${sha.slice(-6)}`;
}

/**
 * A ceiling as the config page names it: "10 MiB", never "10.0 MiB" — and a
 * hand-edited ceiling that is not whole mebibytes falls back to the decimal.
 */
export function formatMiB(n: number): string {
  if (Number.isFinite(n) && n > 0 && n % MIB === 0) return `${n / MIB} MiB`;
  return formatBytes(n);
}
